import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {noop} from '@Utils/general.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {LogoWithBackBtn} from '@Components/logo-with-back-btn';
import {
  AddAccountEvent,
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  ContinueEditingEvent,
  socialAccountPostCoverValidations,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  useAccountIdsToPublishTo,
  useAccountsToPublishTo,
  useNextButtonDisabled,
  useNextButtonIcon,
  useNextButtonText,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardPost,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {PopupMenuItem} from '@Components/popup-menu/components';
import {PopupMenu} from '@Components/popup-menu';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ViewToggleBtnConfig} from '@Components/social-media-wizard/components/social-media-wizard-view-toggle';
import {SocialMediaWizardViewToggle} from '@Components/social-media-wizard/components/social-media-wizard-view-toggle';
import {
  resetStateToDraft,
  setWizardMobileNavView,
  updateNextButtonIcon,
  updateNextButtonText,
  updatePost,
  updatePublishingAction,
  updateTriggerNext,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {CalendarDateTimePicker} from '@Components/calendar-date-time-picker';
import {Popover} from '@Components/popover';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import {
  CACHE_KEY_FOR_SCHEDULING,
  convertDateToUTCTimestamp,
  getCaptionErrorAccountId,
  getTitleErrorAccountId,
  showSocialMediaPublishingUpsellDialog,
  SocialMediaUpsellEvent,
} from '@Libraries/social-media';
import {formatDateFromTimestamp, formatLocalDate, isToday} from '@Utils/date.util';
import {Text, TextSize} from '@Components/text';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {
  useAreCaptionsBeingAutoGenerated,
  useHasPublishingFinished,
  useHideNextButton,
  useIsTemplateSelected,
  useWizardCaptionErrors,
  useWizardDesignsGraphicsMetaMap,
  useWizardPublishingAction,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {getPremiumUpsellIconClasses} from '@Libraries/premium-library';
import {LoadingStates} from '@Utils/loading.util';
import {
  getFirstWizardStep,
  hasUserSeenSMPPreviewCtaPulseForAddCover,
  hasUserSeenSMPViewToggleHighlightForAddCover,
  markUserHasSeenSMPViewToggleHighlightForAddCover,
  markUserSeenSMPPreviewCtaPulseForAddCover,
} from '@Libraries/social-media-wizard-library';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {useAppDispatch} from '@/hooks';
import styles from './social-media-wizard-nav.module.scss';
import {SocialMediaProgressBar, SocialMediaProgressBarSize} from '../../../social-media/components/social-media-progress-bar';
import {showLoading} from '@Libraries/loading-toast-library';
import type {DesignSelection} from '@Components/social-media/social-media.types';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 1040,
  },
  large: {
    minWidth: 1040,
  },
  xlarge: {
    minWidth: 1200,
  },
};

const ANIMATABLE_ICON_CLASS = 'icon-publish-arrow';

interface SocialMediaWizardNavProps {
  navSteps?: string[];
  onNext?: VoidFunction;
  onBack?: VoidFunction;
  currentSelectedDesigns?: DesignSelection[];
}

export function SocialMediaWizardNav({navSteps = [], onNext = noop, onBack = noop, currentSelectedDesigns = []}: SocialMediaWizardNavProps): ReactElement<any> {
  const isNextButtonDisabled = useNextButtonDisabled();
  const nextButtonStateText = useNextButtonText();
  const nextButtonStateIcon = useNextButtonIcon();
  const dispatch = useAppDispatch();
  const nextButtonDefaultText = window.i18next.t('pmwjs_next');
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const [isDateTimePickerVisible, setIsDateTimePickerVisible] = useState(false);
  const showBrowserConfirmOnExit = useRef<boolean>(false);
  const currNavStep = useRef<number>(0);
  const newNavStep = useRef<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const socialPost = useSocialMediaWizardPost();
  const allPublishingStatus = usePublishingStatusForAllAccounts();
  const publishingAction = useWizardPublishingAction();
  const currentStepFromRedux = useWizardStep();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const accountsToPublishTo = useAccountsToPublishTo();
  const designGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const titleErrors = useWizardTitleErrors();
  const hideNextButton = useHideNextButton();
  const captionErrors = useWizardCaptionErrors();
  const hasPublishingFinished = useHasPublishingFinished();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const previousWizardStep = useRef(getFirstWizardStep());
  const [showHightlightPulseForViewToggle, setShowHighlightPulseForViewToggle] = useState(false);
  const [showPulsatingDotForPreviewCTA, setShowPulsatingDotForPreviewCTA] = useState(false);
  const VIEW_TOGGLE_HIGHLIGHT_VISIBILITY_TIME_MS = 6000;
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();

  let nextButtonText = nextButtonDefaultText;
  let nextButtonIcon = nextButtonStateIcon;

  if (nextButtonStateIcon) {
    nextButtonIcon = nextButtonStateIcon;
  }

  if (nextButtonStateText) {
    nextButtonText = nextButtonStateText;
  }

  const resetNavState = (): void => {
    dispatch(setWizardMobileNavView(WizardMobileNavView.CONTENT));
  };

  const didAllAccountsErrorOut = (): boolean => {
    let allAccountsErroredOut = true;

    if (!allPublishingStatus) {
      return false;
    }

    accountIdsToPublishTo.forEach((accountIdToPublishTo) => {
      const publishingStatusForAccount = allPublishingStatus[accountIdToPublishTo];

      if (!publishingStatusForAccount) {
        return;
      }

      const hasSomePostBeenPublished =
        publishingStatusForAccount[PlatformMediaType.POST] !== LoadingStates.NOT_LOADED ||
        publishingStatusForAccount[PlatformMediaType.REELS] !== LoadingStates.NOT_LOADED ||
        publishingStatusForAccount[PlatformMediaType.STORIES] !== LoadingStates.NOT_LOADED;

      if (allAccountsErroredOut && hasSomePostBeenPublished) {
        allAccountsErroredOut = false;
      }
    });

    return allAccountsErroredOut;
  };

  const onLogoClick = (): void => {
    if (showBrowserConfirmOnExit.current) {
      const shouldLeave = window.confirm(window.i18next.t('pmwjs_you_have_unsaved_changes'));
      if (!shouldLeave) {
        return;
      }

      showBrowserConfirmOnExit.current = false;
    }

    const loadingKey = 'mystuff-redirect';
    const saveToDraftWait = 1000;
    showLoading(loadingKey, {text: window.i18next.t('pmwjs_redirecting_to_my_stuff')});
    savePostAsDraftIfNeeded();

    setTimeout(() => {
      window.location.href = window.PMW.util.site_url('');
    }, saveToDraftWait);
  };

  const savePostAsDraftIfNeeded = (): void => {
    if (!publishingAction || publishingAction === SocialPostPublishingAction.SAVE) {
      dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
      dispatch(updateTriggerNext());
    }
  };

  const onBackClick = (): void => {
    resetNavState();

    if (didAllAccountsErrorOut()) {
      dispatch(resetStateToDraft());
    }

    onBack();
  };

  const doHideBackButton = (): boolean => {
    return isFinalStep() && !didAllAccountsErrorOut();
  };
  const isFinalStep = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.PUBLISH;
  };

  const isMobileScreen = (): boolean => {
    return params.xsmall || params.small;
  };

  const getLogoWithButton = (): ReactElement<any> => {
    return <LogoWithBackBtn onBack={onBackClick} isMobileLogo={isMobileScreen()} onLogoClick={onLogoClick} hideBackButton={doHideBackButton()} />;
  };

  const getProgressBar = (): ReactElement<any> | null => {
    if (isMobileScreen()) {
      return null;
    }

    return (
      <SocialMediaProgressBar
        size={params.medium ? SocialMediaProgressBarSize.SMALL : SocialMediaProgressBarSize.LARGE}
        className={styles.navSteps}
        currentStep={currNavStep.current}
        labels={navSteps}
        newStep={newNavStep.current}
      />
    );
  };

  const getIconMenuMarkup = (): ReactElement => {
    if (params.medium && shouldShowScheduleDetails()) {
      return <Icon icon="icon icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={areCaptionsBeingAutoGenerated} />;
    }
    return (
      <Icon
        id="publish-options-menu-icon"
        className="spacing-m-r-2"
        icon="icon-more-h"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.GHOST}
        isDisabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const onSchedule = (): void => {
    if (!isUserPremium()) {
      showSocialMediaPublishingUpsellDialog(SocialMediaUpsellEvent.SCHEDULE);
      return;
    }

    onScheduleForLater();
  };

  const getSaveAsDraftButton = (): ReactElement<any> => {
    return (
      <Button
        customClasses="spacing-m-r-2"
        type={Type.DARK_SECONDARY}
        size={Size.SMALL}
        onClick={onSaveAsDraft}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-save"
        text={window.i18next.t('pmwjs_draft')}
        disabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const getScheduleDetailsPanelMenu = (): ReactElement<any> => {
    let saveAsDraftButton = null;

    if (params.xlarge) {
      saveAsDraftButton = getSaveAsDraftButton();
    }

    return (
      <>
        {saveAsDraftButton}
        {getScheduleDetailsButton()}
      </>
    );
  };

  const getDetailsPanelMenu = (): ReactElement<any> => {
    let scheduleIconClasses = 'icon icon-calendar';
    const isPAYGUser = !isUserPremium();

    if (isPAYGUser) {
      scheduleIconClasses = getPremiumUpsellIconClasses();
    }

    if (params.xlarge) {
      return (
        <>
          {getSaveAsDraftButton()}
          <Button
            customClasses="spacing-m-r-2"
            type={isPAYGUser ? Type.PREMIUM : Type.DARK_SECONDARY}
            size={Size.SMALL}
            onClick={onSchedule}
            iconPlacement={IconPlacement.LEFT}
            icon="icon-calendar"
            text={window.i18next.t('pmwjs_schedule')}
            disabled={areCaptionsBeingAutoGenerated}
          />
        </>
      );
    }

    return (
      <div id="popup-menu">
        <PopupMenu menuToggleButton={getIconMenuMarkup()} maxWidth={undefined} scrollingContainerSelector={undefined}>
          <PopupMenuItem text={window.i18next.t('pmwjs_save_as_draft')} iconClasses="icon icon-save" clickHandler={onSaveAsDraft} />
          <PopupMenuItem text={window.i18next.t('pmwjs_schedule_for_later')} iconClasses={scheduleIconClasses} clickHandler={onSchedule} />
        </PopupMenu>
      </div>
    );
  };

  const shouldShowScheduleDetails = (): boolean => {
    if (!socialPost?.state) {
      return false;
    }

    if (
      socialPost.state === PostState.SCHEDULED ||
      socialPost.state === PostState.PUBLISHING ||
      socialPost.state === PostState.DRAFT ||
      socialPost.state === PostState.ERROR ||
      socialPost.state === PostState.SCHEDULE_ERROR
    ) {
      return false;
    }

    return !!(socialPost?.publishOn && socialPost.timeZoneOffset !== null && currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS);
  };

  const getDateString = (dateTimeString: string): string[] => {
    const parts = dateTimeString.split(', ');

    const datePart = `${parts[0]}, ${parts[1]}`;
    const timePart = parts[2];

    return [datePart, timePart];
  };

  const getScheduleContainer = (): null | ReactElement<any> => {
    if (!socialPost?.publishOn) {
      return null;
    }

    const stringDate = formatLocalDate(socialPost.publishOn);
    const dateTimeArray = getDateString(stringDate);
    const time = dateTimeArray[1];
    const date = formatDateFromTimestamp(socialPost.publishOn);

    return (
      <div className="flex-column-justify-between">
        <Text val={date} bold size={TextSize.XSMALL} className="content-body-white" />
        <Text val={time} bold size={TextSize.XXSMALL} className="color-extra-light" />
      </div>
    );
  };

  const unschedulePost = (): void => {
    if (socialPost === undefined) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/unschedulePost'), {idPost: socialPost.id});
  };

  const doDeleteScheduling = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      const {publishOn: _, timeZoneOffset: __, state: ___, ...unscheduledPost} = socialPost;
      const draftPost = {...unscheduledPost, state: PostState.DRAFT};
      deletePrefilledDate();
      unschedulePost();
      dispatch(updatePost(draftPost));
    }
  };

  const onDeleteSchedule = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      doDeleteScheduling();
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_publish')));
      dispatch(updateNextButtonIcon('icon-publish-arrow'));
    }
  };

  const onEditClick = (): void => {
    setIsDateTimePickerVisible(true);
  };

  const getEditIconsGroup = (): ReactElement<any> => {
    return (
      <>
        <Icon
          onClick={(): void => {
            onDeleteSchedule();
          }}
          icon="icon icon-delete"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          className="spacing-m-r-2"
        />
        <Icon
          onClick={(): void => {
            onEditClick();
          }}
          icon="icon icon-pencil"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARKER}}
        />
      </>
    );
  };

  const getEditScheduleMenu = (): ReactElement<any> => {
    return (
      <PopupMenu menuToggleButton={getIconMenuMarkup()} maxWidth={undefined} scrollingContainerSelector={undefined}>
        <PopupMenuItem
          text={window.i18next.t('pmwjs_reschedule')}
          iconClasses="icon icon-pencil"
          clickHandler={(): void => {
            setIsDateTimePickerVisible(true);
          }}
        />
        <PopupMenuItem text={window.i18next.t('pmwjs_remove')} iconClasses="icon icon-delete" clickHandler={onDeleteSchedule} />
      </PopupMenu>
    );
  };

  // 2 of 3 published

  const getScheduleDetailsButton = (): ReactElement<any> => {
    return (
      <div id="add-details-onboarding-step-three" className={`_full-width spacing-m-r-2 flex-align-items-center flex-row-justify-between ${styles.scheduleContainer}`}>
        <div className="flex-h-row flex-align-items-center spacing-m-r-2">{getScheduleContainer()}</div>
        <div className="flex-h-row">{params.large ? getEditIconsGroup() : getEditScheduleMenu()}</div>
      </div>
    );
  };

  const triggerPreviousOnAddAccount = (): void => {
    onBackClick();
  };

  const triggerPreviousOnContinueEditing = (): void => {
    resetNavState();
  };

  useEffect(() => {
    window.addEventListener(AddAccountEvent.ADD_ACCOUNT, triggerPreviousOnAddAccount);
    window.addEventListener(ContinueEditingEvent.CONTINUE_EDITING, triggerPreviousOnContinueEditing);
    window.addEventListener('beforeunload', confirmNavigation);

    return (): void => {
      window.removeEventListener(AddAccountEvent.ADD_ACCOUNT, triggerPreviousOnAddAccount);
      window.removeEventListener(ContinueEditingEvent.CONTINUE_EDITING, triggerPreviousOnContinueEditing);
      window.removeEventListener('beforeunload', confirmNavigation);
    };
  }, []);

  const confirmNavigation = (event: BeforeUnloadEvent): void => {
    if (showBrowserConfirmOnExit.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };

  const resetPreviewCTAPulsesStates = (): void => {
    if (!isMobileScreen()) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
    setShowHighlightPulseForViewToggle(false);
  };

  useDidUpdateEffect(() => {
    showBrowserConfirmOnExit.current = currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS;

    resetNavState();
    resetPreviewCTAPulsesStates();
    triggerPreviewCTAPulsesIfNeeded();
  }, [currentStepFromRedux]);

  const isWizardStepInAccountsPhase = (wizardStep: SocialMediaWizardStep): boolean => {
    return (
      wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ||
      wizardStep === SocialMediaWizardStep.ACCOUNT_CONNECTION ||
      wizardStep === SocialMediaWizardStep.ADD_DETAILS ||
      wizardStep === SocialMediaWizardStep.CONNECT_ACCOUNTS
    );
  };

  const isCurrentWizardStepMovingNext = (): boolean => {
    const currentWizardStepKey = Object.keys(SocialMediaWizardStep).indexOf(currentStepFromRedux);
    const previousWizardStepKey = Object.keys(SocialMediaWizardStep).indexOf(previousWizardStep.current);
    return currentWizardStepKey >= previousWizardStepKey;
  };

  useEffect((): void => {
    if (isWizardStepInAccountsPhase(currentStepFromRedux) && isWizardStepInAccountsPhase(previousWizardStep.current)) {
      return;
    }

    const isMovingForward = isCurrentWizardStepMovingNext();

    if (currentStepFromRedux === previousWizardStep.current && (isSelectTemplateFlow || navSteps?.length === 2)) {
      return;
    }

    if (isSelectTemplateFlow && currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS) {
      return;
    }

    previousWizardStep.current = currentStepFromRedux;

    if (isMovingForward) {
      newNavStep.current += 1;
    } else {
      newNavStep.current -= 1;
    }
  }, [currentStepFromRedux]);

  useDidUpdateEffect((): void => {
    currNavStep.current = newNavStep.current;
  }, [newNavStep.current]);

  const onPremiumUpsell = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING as string);
  };

  const onNextButtonClick = (): void => {
    if (shouldShowPremiumUpselling()) {
      onPremiumUpsell();
      return;
    }

    onNext();
  };

  const getBackToMyStuffButton = (): ReactElement<any> => {
    return (
      <Button
        disabled={!hasPublishingFinished}
        customClasses="-noun"
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_back_to_my_posts')}
        onClick={(): void => {
          window.location.href = window.PMW.util.site_url('posters/mine#/posts');
        }}
      />
    );
  };

  const getIconSpecificButtonClasses = (): string => {
    if (nextButtonIcon === ANIMATABLE_ICON_CLASS) {
      return 'publish-icon-hover-animation';
    }

    return '';
  };

  const onClickContentOption = (): void => {
    dispatch(setWizardMobileNavView(WizardMobileNavView.CONTENT));
  };

  const onClickPreviewOption = (): void => {
    hidePulsatingDotForPreviewCTAIfNeeded();
    dispatch(setWizardMobileNavView(WizardMobileNavView.PREVIEW));
  };

  const isPostEligibleForCover = (): boolean => {
    const isSingleGraphic = Object.values(designGraphicsMetaMap).length === 1 && Object.values(Object.values(designGraphicsMetaMap)[0]).length === 1;

    if (!isSingleGraphic) {
      return false;
    }

    const isVideo = Object.values(Object.values(designGraphicsMetaMap)[0])[0].isVideo;
    if (!isVideo) {
      return false;
    }

    const accountsSupportingPostCoverToPublishTo = accountsToPublishTo.filter((account) => socialAccountPostCoverValidations[account.type]?.doesSupportCover);

    return accountsSupportingPostCoverToPublishTo.length >= 1;
  };

  const showNewFeaturePulseForAddCoverCTA = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS && isPostEligibleForCover();
  };

  const shouldTriggerPreviewCTAPulses = (): boolean => {
    if (!isMobileScreen()) {
      return false;
    }

    return showNewFeaturePulseForAddCoverCTA();
  };

  const triggerPreviewCTAPulsesIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses()) {
      return;
    }

    void updateShowPulsatingDotForPreviewCtaState();
    void updateShowHightlightPulseForViewToggleState();
  };

  const hidePulsatingDotForPreviewCTAIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses() || !showPulsatingDotForPreviewCTA) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
    markUserSeenSMPPreviewCtaPulse();
  };

  const markUserSeenSMPPreviewCtaPulse = (): void => {
    if (showNewFeaturePulseForAddCoverCTA()) {
      void markUserSeenSMPPreviewCtaPulseForAddCover();
    }
  };

  const markUserHasSeenSMPViewToggleHighlight = (): void => {
    if (showNewFeaturePulseForAddCoverCTA()) {
      void markUserHasSeenSMPViewToggleHighlightForAddCover();
    }
  };

  const setTimerForUpdatingUserHasSeenViewToggleHighlight = (): void => {
    setTimeout(() => {
      setShowHighlightPulseForViewToggle(false);
      markUserHasSeenSMPViewToggleHighlight();
    }, VIEW_TOGGLE_HIGHLIGHT_VISIBILITY_TIME_MS);
  };

  const updateShowHightlightPulseForViewToggleState = async (): Promise<void> => {
    let hasUserSeen = true;

    if (showNewFeaturePulseForAddCoverCTA()) {
      hasUserSeen = await hasUserSeenSMPViewToggleHighlightForAddCover();
    }

    if (hasUserSeen) {
      return;
    }

    setShowHighlightPulseForViewToggle(true);
    setTimerForUpdatingUserHasSeenViewToggleHighlight();
  };

  const updateShowPulsatingDotForPreviewCtaState = async (): Promise<void> => {
    let hasUserSeen = true;

    if (showNewFeaturePulseForAddCoverCTA()) {
      hasUserSeen = await hasUserSeenSMPPreviewCtaPulseForAddCover();
    }

    if (hasUserSeen) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(true);
  };

  const getButtonsForViewToggle = (): ViewToggleBtnConfig[] => {
    const isSelectAccountsStep = currentStepFromRedux === SocialMediaWizardStep.SELECT_ACCOUNTS;
    const isAddDetailsStep = currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS;

    return [
      {
        text: isSelectAccountsStep ? window.i18next.t('pmwjs_accounts') : window.i18next.t('pmwjs_caption'),
        onClick: onClickContentOption,
        showPulsatingDot: false,
      },
      {
        text: window.i18next.t('pmwjs_preview'),
        onClick: onClickPreviewOption,
        showPulsatingDot: isAddDetailsStep || isSelectAccountsStep ? showPulsatingDotForPreviewCTA : false,
      },
    ];
  };

  const shouldShowPulsatingHighlight = (): boolean => {
    if (!shouldTriggerPreviewCTAPulses()) {
      return false;
    }

    return showHightlightPulseForViewToggle;
  };

  const getMobileViewToggle = (): ReactElement<any> | null => {
    if (currentStepFromRedux !== SocialMediaWizardStep.SELECT_DESIGN) {
      return (
        <SocialMediaWizardViewToggle
          key={currentStepFromRedux}
          buttons={getButtonsForViewToggle()}
          buttonSize={Size.XSMALL}
          showPulsatingHighlight={shouldShowPulsatingHighlight()}
        />
      );
    }
    return null;
  };

  const getNavButtons = (): ReactElement<any> | null => {
    if (isFinalStep()) {
      return getBackToMyStuffButton();
    }

    if (params.xlarge || params.large || params.medium) {
      return getNextButtonWeb();
    }

    return getMobileViewToggle();
  };

  const onSaveAsDraft = (): void => {
    doDeleteScheduling();

    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
    dispatch(updateTriggerNext());
  };

  const onSchedulingDone = (): void => {
    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SCHEDULE));
    dispatch(updateTriggerNext());
  };

  const onScheduleForLater = (): void => {
    setIsDateTimePickerVisible(true);
  };

  const onScheduleDetails = (dateTime: Date): void => {
    closeDateTimePicker();
    if (socialPost !== undefined) {
      const timeZoneOffset = dateTime.getTimezoneOffset();
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };
      dispatch(updatePost(newPost));
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_schedule')));
      dispatch(updateNextButtonIcon('icon-calendar'));
    }
  };

  const closeDateTimePicker = (): void => {
    setIsDateTimePickerVisible(false);
  };

  const getPrefilledDate = (): Date => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (prefilledDate) {
      return new Date(prefilledDate);
    }

    return new Date();
  };

  const shouldSkipDateSelector = (): boolean => {
    const prefilledDate = getPrefilledDate();

    return !isToday(prefilledDate);
  };

  const deletePrefilledDate = (): void => {
    window.PMW.deleteCacheItem(CACHE_KEY_FOR_SCHEDULING);
  };

  const showDateTimePicker = (): ReactElement<any> | null => {
    if (isDateTimePickerVisible)
      return (
        <Popover referenceElementRef={ref} popoverCloseFunction={closeDateTimePicker}>
          <div className={styles.dateTimePickerContainer}>
            <CalendarDateTimePicker
              prefilledDate={getPrefilledDate()}
              skipDateSelector={shouldSkipDateSelector()}
              timePickerCtaText={window.i18next.t('pmwjs_done')}
              onSubmit={(dateTime: Date): void => {
                onScheduleDetails(dateTime);
                deletePrefilledDate();
              }}
            />
          </div>
        </Popover>
      );

    return null;
  };

  const shouldShowPremiumUpselling = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.SELECT_DESIGN && !isUserPremium() && currentSelectedDesigns.length > 1;
  };

  const getNavNextButton = (): ReactElement<any> => {
    return (
      <Button
        customClasses={`${hideNextButton ? '_hidden' : ''} ${getIconSpecificButtonClasses()} -noun`}
        disabled={isNextButtonDisabled || areCaptionsBeingAutoGenerated}
        type={shouldShowPremiumUpselling() ? Type.PREMIUM : Type.SECONDARY}
        size={Size.SMALL}
        icon={nextButtonIcon}
        text={nextButtonText}
        onClick={shouldShowScheduleDetails() ? onSchedulingDone : onNextButtonClick}
        iconPlacement={shouldShowScheduleDetails() ? IconPlacement.LEFT : IconPlacement.RIGHT}
      />
    );
  };

  const getMultiPostUpsellToolTip = (): ReactElement<any> => {
    return (
      <div className="flex-center">
        <Icon size={IconSize.SIZE_ICON_16} icon="icon-crown" className="_unmargin-left spacing-m-r-1 spacing-m-b-1" />
        <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_upsell_multi_posting')} />
      </div>
    );
  };

  const getNextButtonWeb = (): ReactElement<any> => {
    return (
      <div className="flex-center _fit-height _fit-width">
        {currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS && !shouldShowScheduleDetails() ? getDetailsPanelMenu() : null}
        {shouldShowScheduleDetails() ? getScheduleDetailsPanelMenu() : null}
        {shouldShowPremiumUpselling() ? (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            delay={{show: 100, hide: 100}}
            overlay={
              <Tooltip className={styles.premiumTooltip} placement="top" id="tooltip-upsell">
                {getMultiPostUpsellToolTip()}
              </Tooltip>
            }
            defaultShow={undefined}
            flip={undefined}
            onHide={undefined}
            onToggle={undefined}
            placement="bottom"
            popperConfig={undefined}
            show={undefined}
            target={undefined}
          >
            <div>{getNavNextButton()}</div>
          </OverlayTrigger>
        ) : (
          getNavNextButton()
        )}
      </div>
    );
  };

  return (
    <div className={BLURABLE_CLASS_IN_AI_CAPTION_MODE}>
      <div ref={containerRef} className={`${styles.nav} _full-width flex-h-row flex-justify-between spacing-p-2 flex-align-items-center`}>
        {getLogoWithButton()}
        {getProgressBar()}
        {getNavButtons()}
      </div>
      {showDateTimePicker()}
      <div className={styles.navSpacer} />
    </div>
  );
}
